import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, X } from "lucide-react";
import { playReceiveSound, playSendSound } from "../lib/sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function cleanChatText(value) {
  return value.replace(/\*\*/g, "").replace(/__/g, "").replace(/`/g, "")
    .replace(/^#{1,6}\s*/gm, "").replace(/^\s*[-*•]\s+/gm, "")
    .replace(/\n{3,}/g, "\n\n").replace(/[ \t]{2,}/g, " ").trim();
}

export default function ChatPanel({ close }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Oi! Eu sou o assistente da EDITVIEIRA. Posso te contar sobre os serviços, pacotes, valores e a promoção de domingo — ou já anotar o seu pedido de orçamento." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [session] = useState(() => crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    const question = input.trim();
    playSendSound();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }]);
    setLoading(true);
    try {
      const response = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: question, session_id: session }),
      });
      if (!response.ok || !response.body) throw new Error("chat unavailable");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let text = "";
      setMessages((m) => [...m, { role: "assistant", text: "" }]);
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        decoder.decode(value).split("\n\n").forEach((part) => {
          if (!part.startsWith("data: ")) return;
          const payload = part.slice(6).trim();
          if (payload === "[DONE]") return;
          try { text += JSON.parse(payload); } catch { text += payload; }
          setMessages((m) => [...m.slice(0, -1), { role: "assistant", text: cleanChatText(text) }]);
        });
      }
      playReceiveSound();
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Fale diretamente com Vieira pelo WhatsApp: (21) 98029-5490." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div className="chat-panel" data-testid="ai-chat-panel"
      initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 30, scale: 0.97 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
      <div className="chat-head">
        <div><span className="online-dot" /> EDITVIEIRA AI <small>assistente criativo</small></div>
        <button onClick={close} data-testid="close-ai-chat-button" aria-label="Fechar chat"><X size={18} /></button>
      </div>
      <div className="chat-messages" ref={scrollRef}>
        {messages.map((message, i) => (
          <div key={i} className={`chat-message ${message.role}`} data-testid={`chat-message-${message.role}-${i}`}>{message.text}</div>
        ))}
        {loading && <div className="chat-typing">digitando<span>...</span></div>}
      </div>
      <form className="chat-input" onSubmit={send}>
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Pergunte sobre os serviços..." data-testid="ai-chat-input" />
        <button type="submit" data-testid="ai-chat-send-button" aria-label="Enviar mensagem"><Send size={17} /></button>
      </form>
    </motion.div>
  );
}
